import MuiAvatar, {
  type AvatarProps as MuiAvatarProps,
} from '@mui/material/Avatar';
import type { Person } from '@tyro/api';
import { forwardRef, useMemo } from 'react';
import { usePreferredNameLayout } from '../../hooks';
import { getColorBasedOnPerson, getColorBasedOnString } from '../../utils';
import { type IconPickerType, iconPickerMap } from '../icon-picker';
import { getInitials } from './utils';

export interface AvatarProps extends Omit<MuiAvatarProps, 'src'> {
  name?: string;
  src?: string | null | undefined;
  icon?: IconPickerType | null | undefined;
  size?: number;
  person?: Pick<Person, 'firstName' | 'lastName'> | null;
  removedColors?: Parameters<typeof getColorBasedOnString>[1];
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    { name, person, sx, children, src, size, removedColors, icon, ...props },
    ref,
  ) => {
    const { displayName } = usePreferredNameLayout();
    const { initials, bgcolor } = useMemo(
      () => ({
        initials: getInitials(name),
        bgcolor: person
          ? getColorBasedOnPerson(displayName, person, removedColors)
          : name && getColorBasedOnString(name, removedColors),
      }),
      [name],
    );
    const IconComponent = iconPickerMap.get(icon!);

    return (
      <MuiAvatar
        ref={ref}
        sx={{
          bgcolor,
          '&:has(> img)': {
            backgroundColor: 'white',
          },
          width: size,
          height: size,
          border: '1px solid',
          borderColor: 'divider',
          fontSize: size ? Math.ceil(size * 0.35) : undefined,
          ...sx,
        }}
        alt={name}
        src={src ?? undefined}
        {...props}
      >
        {IconComponent ? <IconComponent className="avatar-icon" /> : initials}
        {children}
      </MuiAvatar>
    );
  },
);

if (process.env.NODE_ENV !== 'production') {
  Avatar.displayName = 'Avatar';
}
